/**
 * Optimistic store writes for CRM event batches.
 *
 * Applies an event batch to the CRM object store before the server confirms it,
 * and rolls that write back if the submit fails. The rollback is seq-guarded so
 * a WebSocket event or refetch that lands mid-flight is never erased.
 *
 * These are plain functions rather than React Query lifecycle callbacks so that
 * more than one mutation hook can share a single implementation:
 *
 * - `useSubmitEventsOptimistic` — batch submits; calls these from onMutate/onError.
 * - `useUpdateCrmProperty` — single-property submits that own a 409 retry loop.
 *
 * A hook that owns a retry loop cannot nest another mutation (each attempt would
 * re-run onMutate, re-snapshotting an already-mutated store), so this logic has
 * to live outside the lifecycle to be reusable at all.
 */

import type {
  AssociationDetail,
  CrmEvent,
  EventBatch,
  PropertyValue,
} from '../../../types/crm';
import type { EntityTypeName } from '../core/types';
import {
  addPendingAssociation,
  addPendingCreation,
  applyAssociationChange,
  applyObjectDeleted,
  applyPropertyUpdate,
  beginBatch,
  clearPendingCreations,
  endBatch,
  getPendingAssociationTargets,
  getPendingCreationsSnapshot,
  getStoredObject,
  peekAssociationDetail,
  removeObject,
  removePendingAssociation,
  restoreStoredObject,
  type StoredObject,
} from './crmObjectStore';

// Monotonic counter appended to optimistic deleted_at timestamps so two rapid
// deletes within the same millisecond produce distinct rollback identities.
// Without this, two mutations that fire in the same ms would share the same
// ISO string, letting a stale onError rollback clear a newer delete.
let optimisticDeleteSeq = 0;

/** A single association add/remove we optimistically applied. */
export interface AppliedAssociation {
  action: 'added' | 'removed';
  associationTypeName: string;
  targetEntityId: string;
  seq: number;
  /**
   * For an `added` op, the exact AssociationDetail object reference the store
   * held right after our optimistic write. Rollback undoes our add only when
   * this exact reference is still present in the store's associations array. A
   * concurrent WS event that removed and re-added the same pair replaces the
   * element with a NEW reference; matching on identity (not on the presence
   * boolean, which lands back at `true` after a WS re-add) keeps that
   * authoritative re-add from being stripped by our rollback. Undefined for
   * `removed` ops — a removal produces no element to track.
   */
  addedRef?: AssociationDetail;
}

/**
 * Everything `rollbackOptimisticBatch` needs to undo an optimistic write
 * without clobbering newer state. Produced by `applyOptimisticBatch`.
 */
export interface OptimisticBatchContext {
  /** Pre-mutation state of entities that already existed in the store. */
  snapshots: Map<string, StoredObject>;
  /** Highest optimistic seq written per pre-existing entity. */
  snapshotSeqs: Map<string, number>;
  /** Highest optimistic seq written per entity that did NOT pre-exist. */
  createdIds: Map<string, number>;
  /** Exact property values we optimistically wrote, per entity. */
  optimisticProps: Map<string, Record<string, PropertyValue>>;
  /** Exact deleted_at timestamp we optimistically wrote, per entity. */
  optimisticDeletes: Map<string, string>;
  /** Association ops we optimistically applied, per entity, for surgical rollback. */
  optimisticAssociations: Map<string, AppliedAssociation[]>;
  /**
   * Pending-association INDEX pairs we optimistically mutated, so rollback knows
   * which pairs to reconcile. Keyed by source entity id → set of target entity
   * ids whose presence in the store's `pendingAssociations` index THIS batch
   * changed (a no-op add/remove that left the index untouched is not recorded).
   * Rollback does NOT blindly restore pre-batch presence for these pairs;
   * instead it re-derives each pair's index presence from the source object's
   * current (already-reverted, seq-guarded) `associations` array. Separate from
   * `optimisticAssociations` (the object's own `associations` array): the index
   * is a distinct frontend structure that list hooks read to inject pending
   * creations into `associated_with_entity_id` filtered lists. Deriving from the
   * array keeps the index consistent with it even when a concurrent optimistic
   * batch legitimately changed the same pair — a blind pre-batch restore would
   * let an older batch's onError clobber that newer write.
   */
  optimisticPendingAssociations: Map<string, Set<string>>;
}

/**
 * Build the property-value record for a property-update or object-created
 * event payload, stamping each value with the event's source so store reads
 * know who wrote it.
 */
function buildPropertyValuesFromPayload(
  event: CrmEvent
): Record<string, PropertyValue> {
  const rawProps = (event.payload['properties'] ?? {}) as Record<
    string,
    unknown
  >;
  const propertyValues: Record<string, PropertyValue> = {};
  for (const [key, value] of Object.entries(rawProps)) {
    propertyValues[key] = {
      value,
      source_type_name: event.source_type_name,
      source_id: event.source_id,
    } as PropertyValue;
  }
  return propertyValues;
}

/**
 * Apply an event batch to the store optimistically.
 *
 * Snapshots the pre-mutation state of every entity the batch touches, then
 * writes the optimistic values. Returns the context needed to roll back.
 *
 * Synchronous by design — callers that also need to cancel in-flight queries
 * should do that themselves before calling this.
 *
 * @param batch  - The events about to be submitted.
 * @param teamId - Team scope for store writes.
 */
export function applyOptimisticBatch(
  batch: EventBatch,
  teamId: number
): OptimisticBatchContext {
  // Snapshot the pre-mutation state of every entity we're about to touch.
  // We capture the full StoredObject (data + meta) so rollback can restore
  // the original completeness flag and seq, not just the data — restoring
  // a list/summary entry as "complete" would let detail hooks read partial
  // list data instead of fetching the full object.
  //
  // We also track entity IDs that did NOT exist in the store before the
  // optimistic write, mapped to the highest optimistic seq we wrote for
  // each. applyPropertyUpdate has created a partial entry (isComplete:
  // false) for these — without rollback, that orphan entry sticks around
  // and its optimistic seq makes later refetches keep merging the failed
  // value back in. Tracking the optimistic seq lets rollback verify that
  // the entry still matches our partial write before removing it, so a
  // WebSocket event or refetch that legitimately populated the entry
  // between apply and rollback is not destroyed.
  const snapshots = new Map<string, StoredObject>();
  const seenForSnapshot = new Set<string>();
  for (const event of batch.events) {
    if (seenForSnapshot.has(event.entity_id)) {
      continue;
    }
    seenForSnapshot.add(event.entity_id);
    const prev = getStoredObject(event.entity_id);
    if (prev) {
      // Deep-clone the data half so post-mutation mutations don't leak
      // into the snapshot. Meta fields are primitives — safe to share.
      snapshots.set(event.entity_id, {
        data: {
          ...prev.data,
          properties: prev.data.properties
            ? { ...prev.data.properties }
            : prev.data.properties,
          associations: prev.data.associations
            ? [...prev.data.associations]
            : prev.data.associations,
        },
        meta: { ...prev.meta },
      });
    }
  }

  // Write optimistic updates to the store so store-backed hooks reflect
  // the change immediately, and the seq-based dedup correctly skips the
  // echoed WS event when it arrives. Track the highest optimistic seq we
  // wrote per touched entity so rollback can verify the entry still
  // matches before reverting:
  //
  // - For snapshotted entities (pre-existed) the seq goes into
  //   `snapshotSeqs`; if the live store seq is strictly higher than our
  //   optimistic seq at rollback time, a concurrent WS event must have
  //   arrived during the mutation and restoring the snapshot would erase
  //   that newer state (`restoreStoredObject` bypasses the seq guard).
  // - For newly-created entities the seq goes into `createdIds`; the
  //   removeObject path then checks `current.lastSeq === optimisticSeq`
  //   so a concurrent WS event leaves the entry intact.
  //
  // The store's seq guard keeps only the highest seq when events arrive
  // out-of-order, so we mirror that with a max here — recording a
  // stale (lower) seq would make rollback's seq match check fail and
  // leave orphan / over-rolled-back state behind.
  const snapshotSeqs = new Map<string, number>();
  const createdIds = new Map<string, number>();
  const optimisticProps = new Map<string, Record<string, PropertyValue>>();
  const optimisticDeletes = new Map<string, string>();
  const optimisticAssociations = new Map<string, AppliedAssociation[]>();
  const optimisticPendingAssociations = new Map<string, Set<string>>();
  const recordOptimisticSeq = (entityId: string, seq: number) => {
    const target = snapshots.has(entityId) ? snapshotSeqs : createdIds;
    const existing = target.get(entityId);
    if (existing === undefined || seq > existing) {
      target.set(entityId, seq);
    }
  };
  // Record that this batch mutated a (source → target) pair in the store's
  // pending-association index, so rollback knows to reconcile it. We record the
  // pair only when the index actually changed (a no-op add/remove is skipped),
  // and rollback re-derives its presence from the object's associations array —
  // so we track the touched pair, not a pre-batch presence flag.
  const recordPendingAssocTouched = (
    sourceEntityId: string,
    targetEntityId: string
  ) => {
    let targets = optimisticPendingAssociations.get(sourceEntityId);
    if (!targets) {
      targets = new Set<string>();
      optimisticPendingAssociations.set(sourceEntityId, targets);
    }
    targets.add(targetEntityId);
  };

  beginBatch();
  try {
    for (const event of batch.events) {
      try {
        if (event.event_type_name === 'ahoy_property_updated') {
          const propertyValues = buildPropertyValuesFromPayload(event);
          const result = applyPropertyUpdate(
            event.entity_id,
            event.entity_type_name as EntityTypeName,
            propertyValues,
            event.seq,
            teamId
          );
          // Only track rollback metadata for a write that actually landed. A
          // 'skipped' (stale seq) or 'gap' result left the store untouched, so
          // recording it would let rollback revert a value we never wrote —
          // or clobber the value a newer event already placed there.
          if (result === 'applied') {
            recordOptimisticSeq(event.entity_id, event.seq);
            // Track the exact property values we optimistically wrote so
            // rollback can surgically revert only properties that still
            // hold our optimistic value (i.e. were NOT overwritten by a
            // concurrent WS event with authoritative server data).
            const prev = optimisticProps.get(event.entity_id) ?? {};
            optimisticProps.set(event.entity_id, {
              ...prev,
              ...propertyValues,
            });
          }
        } else if (event.event_type_name === 'ahoy_object_deleted') {
          optimisticDeleteSeq += 1;
          const deletedAt = `${new Date().toISOString()}#${optimisticDeleteSeq}`;
          const result = applyObjectDeleted(
            event.entity_id,
            event.seq,
            deletedAt
          );
          // Only track rollback metadata for a delete that actually landed.
          // A 'skipped' stale delete left the store's deleted_at untouched, so
          // recording our timestamp would let rollback clear a delete we never
          // applied.
          if (result === 'applied') {
            recordOptimisticSeq(event.entity_id, event.seq);
            // Track our exact optimistic delete timestamp so rollback can
            // verify the current store value still equals what we wrote
            // before clearing it. If a newer authoritative or optimistic
            // delete advanced the store, current.data.deleted_at will
            // hold a different timestamp and we must leave it alone.
            optimisticDeletes.set(event.entity_id, deletedAt);
          }
        } else if (event.event_type_name === 'ahoy_object_created') {
          const propertyValues = buildPropertyValuesFromPayload(event);
          // applyPropertyUpdate creates a partial (isComplete:false) entry for an
          // unknown entity — exactly the store contract for an optimistically
          // created object. Deliberate divergence from applyEvent.ts:534 (which
          // builds `{ value }` only): we have the acting source here and stamp it,
          // matching this file's property branch. Partial entries are guarded by
          // isComplete:false and superseded by the authoritative fetch/WS, so the
          // richer shape is safe.
          const result = applyPropertyUpdate(
            event.entity_id,
            event.entity_type_name as EntityTypeName,
            propertyValues,
            event.seq,
            teamId
          );
          if (result === 'applied') {
            recordOptimisticSeq(event.entity_id, event.seq);
            addPendingCreation(event.entity_id);
          }
        } else if (
          event.event_type_name === 'ahoy_association_added' ||
          event.event_type_name === 'ahoy_association_removed'
        ) {
          const associationTypeName = event.payload['association_type_name'] as
            | string
            | undefined;
          const targetEntityId = event.payload['target_entity_id'] as
            | string
            | undefined;
          if (associationTypeName && targetEntityId) {
            const action =
              event.event_type_name === 'ahoy_association_added'
                ? 'added'
                : 'removed';
            // forceNewReference: an idempotent optimistic re-add of an
            // already-present pair must swap in a FRESH AssociationDetail
            // reference. Rollback distinguishes each batch's write purely by
            // reference identity (revertAssociations → working.includes(addedRef)).
            // Without a fresh reference, an OLDER in-flight batch's rollback would
            // reference-match the element a NEWER batch just re-confirmed and strip
            // a successful add whose WS echo is already deduped by the newer seq.
            const result = applyAssociationChange(
              event.entity_id,
              action,
              associationTypeName,
              targetEntityId,
              event.seq,
              { forceNewReference: true }
            );
            // applyAssociationChange returns 'skipped' when the entity isn't in
            // the store; only record for a write that actually landed.
            if (result === 'applied') {
              recordOptimisticSeq(event.entity_id, event.seq);
              let ops = optimisticAssociations.get(event.entity_id);
              if (!ops) {
                ops = [];
                optimisticAssociations.set(event.entity_id, ops);
              }
              ops.push({
                action,
                associationTypeName,
                targetEntityId,
                seq: event.seq,
                // Capture the exact element our add produced (batch-aware read),
                // so rollback can reference-match it against the live store and
                // leave a concurrent WS re-add — which swaps in a new element —
                // untouched. Undefined for removals (nothing to track).
                addedRef:
                  action === 'added'
                    ? peekAssociationDetail(
                        event.entity_id,
                        associationTypeName,
                        targetEntityId
                      )
                    : undefined,
              });
            }
            // Keep the store's pending-association index in step with the
            // association write by DERIVING the pair's presence from the
            // resulting `associations` array (a batch-aware read via
            // peekAssociationDetail), NOT from the event action. This mirrors
            // EventsClient.processOptimisticUpdates. applyAssociationChange only
            // touches the object's own `associations` array; list hooks that
            // filter by `associated_with_entity_id` inject a pending creation
            // solely from this index (see hooks.ts → getPendingAssociationTargets).
            // Without it, an optimistically created record associated with a
            // target would be absent from that filtered list until a refetch.
            //
            // Deriving from the array (rather than the action) is what keeps the
            // two representations consistent when the array write did NOT land: a
            // stale-seq or gap result leaves the array UNCHANGED, so a skipped
            // REMOVAL must not drop a still-associated pending record from the
            // filtered list, and a skipped ADD must not inject a pair the array
            // never gained. The index is only ever read for entities in the
            // pending-creations set, so a non-pending source contributes nothing;
            // a pending source's index mirrors exactly what its (optimistic)
            // associations array holds — covering a record created earlier in
            // THIS batch (addPendingCreation ran above) or in an EARLIER batch
            // (still pending, not yet reconciled) that this batch associates, and
            // excluding a pair a later event in the same batch removed.
            //
            // Whenever the index actually changes, record the touched pair so
            // rollback can reconcile it (see rollbackOptimisticBatch, which
            // re-derives presence from the reverted array). Without this, a
            // failed batch would leave an injected pair behind for a pre-existing
            // pending entity — its `associations` array gets surgically reverted,
            // but the index entry would survive and keep the record
            // phantom-visible in filtered lists.
            const sourceIsPending = getPendingCreationsSnapshot().has(
              event.entity_id
            );
            const pairPresentInArray =
              peekAssociationDetail(
                event.entity_id,
                associationTypeName,
                targetEntityId
              ) !== undefined;
            const shouldIndex = sourceIsPending && pairPresentInArray;
            const wasIndexed =
              getPendingAssociationTargets(event.entity_id)?.has(
                targetEntityId
              ) ?? false;
            if (shouldIndex !== wasIndexed) {
              recordPendingAssocTouched(event.entity_id, targetEntityId);
              if (shouldIndex) {
                addPendingAssociation(event.entity_id, targetEntityId);
              } else {
                removePendingAssociation(event.entity_id, targetEntityId);
              }
            }
          } else {
            console.warn(
              '[optimisticBatch] Association event missing association_type_name or target_entity_id',
              {
                entityId: event.entity_id,
                eventTypeName: event.event_type_name,
              }
            );
          }
        } else {
          console.warn(
            '[optimisticBatch] Unhandled event type in optimistic batch',
            { entityId: event.entity_id, eventTypeName: event.event_type_name }
          );
        }
      } catch (err) {
        console.warn(
          '[optimisticBatch] Failed to apply optimistic store update for event',
          event.event_type_name,
          err
        );
      }
    }
  } finally {
    endBatch();
  }

  return {
    snapshots,
    snapshotSeqs,
    createdIds,
    optimisticProps,
    optimisticDeletes,
    optimisticAssociations,
    optimisticPendingAssociations,
  };
}

/** Parameters for a single-property optimistic store write. */
export interface OptimisticPropertyUpdate {
  entityId: string;
  entityTypeName: string;
  propertyName: string;
  value: unknown;
  /**
   * The seq the submitted event will carry (builder startSeq + 1). Using the
   * same seq means the WS echo of a successful submit is deduped by the
   * store's seq guard rather than re-applied.
   */
  seq: number;
  /** The acting user's id, stamped as source_id on the optimistic value. */
  sourceId: string;
  teamId: number;
}

/** A many-to-one association swap's implicit removal of the prior target. */
export interface SingleCardinalityReplacement {
  entityId: string;
  associationTypeName: string;
  priorTargetEntityId: string;
  /**
   * The NEW target this swap added. The prior target is stripped only when
   * this exact target's add both landed in the rollback context AND survives
   * as the entity's final state — see `applySingleCardinalityReplacements`.
   */
  newTargetEntityId: string;
}

/**
 * Fold a many-to-one association swap's implicit removal of the prior target
 * into an already-applied optimistic batch, so BOTH rollback paths reverse
 * it: the snapshot-restore path (no concurrent WS event) AND the surgical
 * `revertAssociations` path (a concurrent WS event landed mid-flight).
 *
 * The wire batch for this kind of swap stays add-only — the backend cascade
 * enforces the many-to-one constraint and replaces the prior target
 * server-side. Call this immediately after `applyOptimisticBatch` for the
 * batch that added the new target, passing the same `context`, so the
 * replacement is folded into that batch's rollback context rather than
 * happening as an untracked side effect afterward.
 *
 * Strips the prior target via `restoreStoredObject` (bypasses the seq guard,
 * leaves `meta`/`lastSeq` untouched so the store's seq stays in sync with the
 * single wire add event) and records a matching `{ action: 'removed' }` op in
 * `context.optimisticAssociations` — the same structure `revertAssociations`
 * consumes for every other association op — so a concurrent-WS-event
 * rollback re-adds the prior target too. Only strips when the prior target
 * is actually present.
 */
export function applySingleCardinalityReplacements(
  context: OptimisticBatchContext,
  replacements: readonly SingleCardinalityReplacement[]
): void {
  for (const {
    entityId,
    associationTypeName,
    priorTargetEntityId,
    newTargetEntityId,
  } of replacements) {
    const current = getStoredObject(entityId);
    if (!current) {
      continue;
    }
    const associations = current.data.associations ?? [];
    // Only strip the prior target if THIS batch's optimistic add of the NEW
    // target actually landed. If the add was seq-skipped (a 'skipped'/'gap'
    // result — a newer seq already governs this entity), the store never gained
    // the new stage, so stripping the prior one would leave the note stage-less
    // on screen. Worse, a skipped add records no optimistic seq, so
    // `rollbackOptimisticBatch` treats the entity as untouched
    // (`ourSeq === undefined` → skipped) and never reverses this strip —
    // permanently dropping the prior stage. Gate on a recorded 'added' op for
    // this association type AND the specific new target so the strip only
    // happens when the entity is in the rollback context and the removal can be
    // undone. Matching the target (not just the type) prevents an add of a
    // DIFFERENT target on the same association type from authorizing this strip.
    const addWasApplied =
      context.optimisticAssociations
        .get(entityId)
        ?.some(
          op =>
            op.action === 'added' &&
            op.associationTypeName === associationTypeName &&
            op.targetEntityId === newTargetEntityId
        ) ?? false;
    if (!addWasApplied) {
      continue;
    }
    // Require the new target to survive as the entity's FINAL state. A batch
    // that added then later removed the new target (net: no new target) leaves
    // an 'added' op recorded above, but the store no longer holds it — stripping
    // the prior target here would leave the association empty. Reading the live
    // associations array reflects every op the batch applied in order, so this
    // is the "no later removal" check the add-op scan alone can't make.
    const newTargetPresent = associations.some(
      a =>
        a.association_type_name === associationTypeName &&
        a.target_entity_id === newTargetEntityId
    );
    if (!newTargetPresent) {
      continue;
    }
    const stillHasPrior = associations.some(
      a =>
        a.association_type_name === associationTypeName &&
        a.target_entity_id === priorTargetEntityId
    );
    if (!stillHasPrior) {
      continue;
    }
    restoreStoredObject({
      data: {
        ...current.data,
        associations: associations.filter(
          a =>
            !(
              a.association_type_name === associationTypeName &&
              a.target_entity_id === priorTargetEntityId
            )
        ),
      },
      meta: { ...current.meta },
    });
    let ops = context.optimisticAssociations.get(entityId);
    if (!ops) {
      ops = [];
      context.optimisticAssociations.set(entityId, ops);
    }
    ops.push({
      action: 'removed',
      associationTypeName,
      targetEntityId: priorTargetEntityId,
      seq: current.meta.lastSeq,
    });

    // Keep the pending-association INDEX in step with the array strip. List
    // hooks inject a pending (optimistically-created, not-yet-reconciled) note
    // into an `associated_with_entity_id`-filtered list SOLELY from this index
    // (see getPendingAssociationTargets). Removing the prior target from the
    // associations array without clearing its index entry leaves the note
    // phantom-visible in the OLD stage's filtered list. Mirror the index write
    // and record the touched pair so rollback re-derives its presence from the
    // reverted associations array (see rollbackOptimisticBatch's index
    // reconciliation), exactly as the array-side removal above is reversed.
    if (getPendingCreationsSnapshot().has(entityId)) {
      const wasIndexed =
        getPendingAssociationTargets(entityId)?.has(priorTargetEntityId) ??
        false;
      if (wasIndexed) {
        let targets = context.optimisticPendingAssociations.get(entityId);
        if (!targets) {
          targets = new Set<string>();
          context.optimisticPendingAssociations.set(entityId, targets);
        }
        targets.add(priorTargetEntityId);
        removePendingAssociation(entityId, priorTargetEntityId);
      }
    }
  }
}

/**
 * Single-property convenience over `applyOptimisticBatch`.
 *
 * Exists for callers that build their submit events elsewhere (e.g.
 * `useUpdateCrmProperty`, whose 409 retry loop rebuilds the event per
 * attempt) and only need the store write + rollback context. The descriptor
 * event constructed here is never submitted — `idempotency_key` and
 * `occurred_at` only satisfy the `CrmEvent` type; `applyOptimisticBatch`
 * does not read them.
 */
export function applyOptimisticPropertyUpdate(
  update: OptimisticPropertyUpdate
): OptimisticBatchContext {
  const event: CrmEvent = {
    entity_id: update.entityId,
    entity_type_name: update.entityTypeName,
    event_type_name: 'ahoy_property_updated',
    seq: update.seq,
    idempotency_key: crypto.randomUUID(),
    occurred_at: new Date().toISOString(),
    payload: { properties: { [update.propertyName]: update.value } },
    source_type_name: 'ahoy_user',
    source_id: update.sourceId,
  };
  return applyOptimisticBatch({ events: [event] }, update.teamId);
}

/** Surgically revert a batch's association ops for one entity. */
function revertAssociations(
  current: readonly AssociationDetail[],
  snapshotAssocs: readonly AssociationDetail[],
  ops: readonly AppliedAssociation[],
  entityId: string
): { associations: AssociationDetail[]; changed: boolean } {
  const presentIn = (
    list: readonly AssociationDetail[],
    typeName: string,
    targetId: string
  ): boolean =>
    list.some(
      (a: AssociationDetail) =>
        a.association_type_name === typeName && a.target_entity_id === targetId
    );
  // Collapse the ops to the distinct (type, target) pairs we touched.
  // Iterating raw ops in sequence is wrong when the same pair is toggled
  // within one batch (e.g. added→removed): replaying each inverse in turn
  // can reintroduce a net-noop association. Instead we derive, per pair,
  // the end state our batch optimistically produced — which, because
  // `applyAssociationChange` is idempotent, is just the LAST op's action —
  // and undo it against the pre-batch SNAPSHOT, exactly mirroring the
  // property surgery above. For a net-add we also carry the exact element
  // reference our write produced (`addedRef`), so the undo can match on
  // identity rather than mere presence.
  const touched = new Map<
    string,
    {
      typeName: string;
      targetId: string;
      endPresent: boolean;
      addedRef: AssociationDetail | undefined;
    }
  >();
  for (const op of ops) {
    touched.set(`${op.associationTypeName} ${op.targetEntityId}`, {
      typeName: op.associationTypeName,
      targetId: op.targetEntityId,
      endPresent: op.action === 'added',
      addedRef: op.action === 'added' ? op.addedRef : undefined,
    });
  }
  let working = [...current];
  let changed = false;
  for (const { typeName, targetId, endPresent, addedRef } of touched.values()) {
    const currentlyPresent = presentIn(working, typeName, targetId);
    const preBatchPresent = presentIn(snapshotAssocs, typeName, targetId);
    if (endPresent) {
      // Our batch's net effect was to ADD this pair.
      if (preBatchPresent) {
        // The add was an idempotent no-op over a pre-existing association —
        // we produced no new element, so there is nothing to undo. Leave
        // whatever the store now holds (WS changes to it win by default).
        continue;
      }
      // Undo our add — but ONLY if the store still holds the exact
      // AssociationDetail object our optimistic write produced. Reference
      // identity (mirroring the property surgery's `cur === ours`)
      // distinguishes "our optimistic add is still live" from "a concurrent
      // authoritative event re-asserted this pair", which swaps in a NEW
      // element reference. The presence boolean cannot tell these apart: an
      // authoritative remove+re-add — or an idempotent re-add of the pair we
      // already added — lands presence back at `true`, and matching on that
      // alone would strip the authoritative value. Authoritative WS delivery
      // (applyEvent.ts) passes `forceNewReference`, so even an idempotent
      // re-add of an already-present pair replaces the element reference —
      // closing the former residual gap where a WS event redundantly
      // re-confirming our exact pair preserved our reference and was wrongly
      // undone.
      const ourAddStillLive =
        addedRef !== undefined && working.includes(addedRef);
      if (!ourAddStillLive) {
        if (currentlyPresent) {
          console.warn(
            '[optimisticBatch] Association rollback skipped — a concurrent WS event replaced our optimistic association; keeping the authoritative value',
            {
              entityId,
              associationTypeName: typeName,
              targetEntityId: targetId,
            }
          );
        }
        continue;
      }
      working = working.filter(
        (a: AssociationDetail) =>
          !(
            a.association_type_name === typeName &&
            a.target_entity_id === targetId
          )
      );
      changed = true;
    } else {
      // Our batch's net effect was to REMOVE this pair. A removal produces no
      // element to reference-track, so fall back to presence matching: if the
      // pair is present now, a concurrent WS event re-added it (presence !=
      // our removed end state) and its authoritative value wins.
      if (currentlyPresent !== endPresent) {
        console.warn(
          '[optimisticBatch] Association rollback skipped — concurrent WS event left a different presence than our optimistic write',
          {
            entityId,
            associationTypeName: typeName,
            targetEntityId: targetId,
          }
        );
        continue;
      }
      if (preBatchPresent === currentlyPresent) {
        continue; // already at the pre-batch state; nothing to undo.
      }
      // Pre-batch present, now absent → restore the snapshot's association.
      //
      // KNOWN BOUNDED EDGE: a removal produces no element to reference-track
      // (unlike the `added` branch above, which matches on `addedRef`), so we
      // fall back to presence. If a concurrent WS event authoritatively removed
      // this SAME pair while our optimistic removal was in flight, the pair is
      // absent for BOTH reasons and presence alone cannot tell them apart — so
      // this restore can briefly resurrect a server-confirmed removal. We do NOT
      // "prefer absence" to avoid this: the identical presence signal also
      // covers the common, deliberately-tested case where an UNRELATED WS event
      // (e.g. a property update) advanced the seq into this surgical path while
      // our removal is still the sole reason the pair is absent — there the
      // restore is required (see the stage-replacement test "the note is never
      // left with NO stage"). With no per-association authoritative version in
      // the store, the two are indistinguishable here. The resurrection is
      // transient: the terminal-failure path (runBackgroundSaveSubmit →
      // failTerminally) unconditionally invalidates + refetches objectDetail,
      // which reconciles the association to server truth.
      const original = snapshotAssocs.find(
        (a: AssociationDetail) =>
          a.association_type_name === typeName &&
          a.target_entity_id === targetId
      );
      working = [
        ...working,
        original ?? {
          association_type_name: typeName,
          target_entity_id: targetId,
        },
      ];
      changed = true;
    }
  }
  return { associations: working, changed };
}

/**
 * Undo an optimistic batch write, preserving any newer state that arrived
 * while the submit was in flight.
 *
 * - For entities that existed pre-mutation and whose store seq has NOT
 *   advanced: restoreStoredObject writes back the full original
 *   snapshot (data + meta including isComplete), bypassing the seq
 *   guard so a restore to an older seq still takes effect.
 * - For entities whose store seq HAS advanced past our optimistic
 *   write: a concurrent WS event or refetch delivered newer data. A
 *   full snapshot restore would silently erase that data. Instead, we
 *   surgically revert only the properties (and deleted_at flag) that
 *   our optimistic write touched, restoring them to their pre-mutation
 *   values from the snapshot while preserving WS-delivered changes.
 * - For entities that did NOT exist pre-mutation: removeObject deletes
 *   the partial entry created by applyPropertyUpdate, so a later
 *   server fetch isn't dragged backwards by the orphaned optimistic seq.
 *   We only remove entries that still match our partial write —
 *   isComplete must still be false (a server fetch would have flipped
 *   it true) and the stored seq must still equal the optimistic seq we
 *   wrote (a real WS event with newer data would have advanced it). If
 *   either check fails, a WebSocket event or refetch populated the
 *   store between apply and rollback and the entry is now legitimate
 *   server-backed data; leaving it alone preserves that.
 *
 * @param context - The context returned by `applyOptimisticBatch`.
 */
export function rollbackOptimisticBatch(context: OptimisticBatchContext): void {
  const hasSnapshots = context.snapshots.size > 0;
  const hasCreated = context.createdIds.size > 0;
  const hasPendingAssoc = context.optimisticPendingAssociations.size > 0;
  if (!hasSnapshots && !hasCreated && !hasPendingAssoc) {
    return;
  }

  beginBatch();
  try {
    for (const [entityId, snapshot] of context.snapshots) {
      const current = getStoredObject(entityId);
      const ourSeq = context.snapshotSeqs.get(entityId);
      // A snapshot is captured for every touched entity up front, but the
      // optimistic write may have been 'skipped' (stale seq) — in which case
      // no optimistic seq was recorded. We never mutated the store on this
      // entity's behalf, so there is nothing to roll back: a blind snapshot
      // restore here would erase newer server/WS state that arrived after the
      // (skipped) write. Leave the current store value untouched.
      if (ourSeq === undefined) {
        continue;
      }
      // If the store advanced past our optimistic seq, a real
      // WebSocket event or refetch arrived while the submit was in
      // flight. A full snapshot restore would erase that newer
      // WS data. Instead, surgically revert only properties that
      // still hold our optimistic value — meaning the WS event
      // didn't overwrite them with authoritative data. Properties
      // that the WS DID touch already have the correct server
      // value and must be left alone.
      if (current && current.meta.lastSeq > ourSeq) {
        const ourProps = context.optimisticProps.get(entityId);
        const ourOptimisticDeletedAt = context.optimisticDeletes.get(entityId);
        let changed = false;
        const snapshotProps = snapshot.data.properties ?? {};
        // Build reverted properties: start from current, but for
        // each key we optimistically wrote that hasn't been
        // overwritten by a WS event, restore the snapshot value
        // (or omit it if the key didn't exist pre-mutation).
        const keysToRemove = new Set<string>();
        const keysToRestore = new Map<string, PropertyValue>();
        if (ourProps) {
          for (const key of Object.keys(ourProps)) {
            const cur = current.data.properties?.[key];
            const ours = ourProps[key];
            // Only revert if the current value still matches what
            // we optimistically wrote (reference equality is
            // sufficient — the store preserves the same object ref
            // unless a WS event replaced it).
            if (cur === ours) {
              if (key in snapshotProps) {
                keysToRestore.set(key, snapshotProps[key]);
              } else {
                keysToRemove.add(key);
              }
              changed = true;
            }
          }
        }
        const revertedProps: Record<string, PropertyValue> = {};
        if (current.data.properties) {
          for (const [k, v] of Object.entries(current.data.properties)) {
            if (keysToRemove.has(k)) {
              continue;
            }
            const restored = keysToRestore.get(k);
            revertedProps[k] = restored !== undefined ? restored : v;
          }
        }
        let revertedDeletedAt = current.data.deleted_at;
        // Only clear deleted_at if the current value is still the
        // exact timestamp WE optimistically wrote. If the store
        // seq advanced and a newer authoritative or optimistic
        // delete landed, current.data.deleted_at will hold that
        // newer timestamp — clearing it would erase a legitimate
        // delete. Comparing the timestamp value (not just the
        // snapshot's null state) keeps stale failed deletes from
        // overwriting newer ones.
        if (
          ourOptimisticDeletedAt !== undefined &&
          current.data.deleted_at === ourOptimisticDeletedAt &&
          snapshot.data.deleted_at === null
        ) {
          revertedDeletedAt = null;
          changed = true;
        }
        let revertedAssociations = current.data.associations;
        const ourAssocOps = context.optimisticAssociations.get(entityId) ?? [];
        if (ourAssocOps.length > 0) {
          const snapshotAssocs = snapshot.data.associations ?? [];
          const result = revertAssociations(
            current.data.associations ?? [],
            snapshotAssocs,
            ourAssocOps,
            entityId
          );
          revertedAssociations = result.associations;
          if (result.changed) {
            changed = true;
          }
        }
        if (changed) {
          restoreStoredObject({
            data: {
              ...current.data,
              properties: revertedProps,
              deleted_at: revertedDeletedAt,
              associations: revertedAssociations,
            },
            meta: { ...current.meta },
          });
        }
        continue;
      }
      restoreStoredObject(snapshot);
    }

    for (const [entityId, optimisticSeq] of context.createdIds) {
      // The pending-creation flag never survives a failed batch, regardless
      // of the store-entry guards below. Either the creation truly failed —
      // keeping it pending would advertise a row that will never confirm —
      // or the guards below prove the entry became server-backed (a fetch
      // flipped isComplete / a WS event advanced the seq), in which case
      // list refetches return it authoritatively and optimistic injection is
      // no longer needed. Previously the flag was only cleared on the
      // removal path, leaving failed creates injectable until the 60s
      // orphan timer in `addPendingCreation` fired.
      // This also deletes the entity's `pendingAssociations` index entry
      // wholesale; the index-revert loop below explicitly tolerates that
      // ("a no-op if already cleared").
      clearPendingCreations([entityId]);
      const current = getStoredObject(entityId);
      if (!current) {
        continue;
      }
      if (current.meta.isComplete) {
        continue;
      }
      if (current.meta.lastSeq !== optimisticSeq) {
        continue;
      }
      // optimisticAssociations recorded for a batch-created entity is intentionally
      // not consulted here — removing the whole object discards its associations.
      removeObject(entityId);
    }
  } finally {
    endBatch();
  }

  // Reconcile pending-association INDEX writes. This runs AFTER endBatch so the
  // object reverts above (snapshot restore, surgical revert, or removeObject)
  // are committed and visible to getStoredObject — the batch working copy is
  // not readable mid-batch. The object's own `associations` array was already
  // reverted above, but the index that list hooks read to inject pending
  // creations into `associated_with_entity_id` filtered lists is a separate
  // structure and must be reconciled too — otherwise a failed batch leaves a
  // pre-existing pending entity phantom-visible under an association it never
  // gained.
  //
  // We do NOT blindly restore each pair's captured pre-batch presence. A
  // concurrent optimistic batch can legitimately write this same index pair
  // (WS events never do, but another in-flight batch's applyOptimisticBatch
  // does), so a blind pre-batch restore would let this older onError clobber
  // that newer write. Instead, derive each touched pair's correct presence from
  // the source object's now-reverted associations array — which was reverted
  // under the array's own seq guard and so already reflects whichever batch's
  // write should win. Mirroring it keeps the index consistent with the array in
  // every ordering:
  //   - source still pending AND its array still associates the target → add.
  //   - otherwise → remove (a no-op if already cleared, e.g. the source was
  //     removed above via createdIds or its pending entry was cleared).
  for (const [
    sourceEntityId,
    targets,
  ] of context.optimisticPendingAssociations) {
    const sourceObject = getStoredObject(sourceEntityId);
    const sourceIsPending = getPendingCreationsSnapshot().has(sourceEntityId);
    for (const targetEntityId of targets) {
      const stillAssociated =
        sourceIsPending &&
        (sourceObject?.data.associations?.some(
          a => a.target_entity_id === targetEntityId
        ) ??
          false);
      if (stillAssociated) {
        addPendingAssociation(sourceEntityId, targetEntityId);
      } else {
        removePendingAssociation(sourceEntityId, targetEntityId);
      }
    }
  }
}
